import { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";

const BINGO_LETTERS = ["B", "I", "N", "G", "O"];

const popIn = keyframes`
    0% { transform: scale(0.2) rotate(-8deg); opacity: 0; }
    60% { transform: scale(1.15) rotate(2deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const flash = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
`;

const fall = keyframes`
    0% { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
    100% { transform: translateY(110vh) rotate(360deg); opacity: 0.8; }
`;

const stamp = keyframes`
    0% { transform: scale(2.4) rotate(-18deg); opacity: 0; }
    70% { transform: scale(0.92) rotate(-8deg); opacity: 1; }
    100% { transform: scale(1) rotate(-8deg); opacity: 1; }
`;

const reel = keyframes`
    0% { transform: translateY(-120%); }
    100% { transform: translateY(0); }
`;

const VARIANTS = [
    {
        id: "pokie",
        background: "radial-gradient(circle at center, #3a0d18 0%, #120308 70%)",
        accent: "#ffd54f"
    },
    {
        id: "confetti",
        background: "linear-gradient(160deg, #0d47a1 0%, #4a148c 100%)",
        accent: "#fff59d"
    },
    {
        id: "stamp",
        background: "linear-gradient(180deg, #1b5e20 0%, #0d2818 100%)",
        accent: "#c8e6c9"
    },
    {
        id: "jackpot",
        background: "radial-gradient(circle at center, #f9a825 0%, #4e342e 55%, #1a120b 100%)",
        accent: "#fff8e1"
    }
];

const CONFETTI_COLORS = ["#ffeb3b", "#ff5252", "#69f0ae", "#40c4ff", "#ff80ab", "#ffffff"];

const chartLine = (count) => {
    if (count <= 1) return "First chart of the day!";
    if (count === 3) return "Three charts. You are on a roll!";
    if (count === 5) return "Five charts. Legendary shift.";
    if (count >= 7 && count % 2 === 1) return `${count} charts today. The chair is lucky.`;
    return `Chart ${count} today.`;
};

export const nextBingoCount = () => {
    const key = "perioBingoToday";
    const today = new Date().toISOString().slice(0, 10);
    let stored = {};
    try {
        stored = JSON.parse(localStorage.getItem(key) || "{}");
    } catch {
        stored = {};
    }
    const count = stored.date === today ? (stored.count || 0) + 1 : 1;
    localStorage.setItem(key, JSON.stringify({ date: today, count }));
    return count;
};

export const pickBingoVariant = () => Math.floor(Math.random() * VARIANTS.length);

/**
 * Full-screen BINGO flash shown for a moment after an examination is confirmed.
 */
export default function BingoCelebration({ variantIndex = 0, count = 1, durationMs = 3000, onDone }) {
    const variant = VARIANTS[variantIndex % VARIANTS.length];

    const confetti = useMemo(
        () =>
            Array.from({ length: 28 }, (_, index) => ({
                id: index,
                left: `${(index * 37) % 100}%`,
                delay: `${(index % 7) * 0.08}s`,
                color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
                size: 8 + (index % 5) * 3
            })),
        []
    );

    useEffect(() => {
        const timer = setTimeout(() => onDone?.(), durationMs);
        return () => clearTimeout(timer);
    }, [durationMs, onDone]);

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 2000,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: variant.background,
                color: "#fff",
                overflow: "hidden"
            }}>
            {variant.id === "confetti" &&
                confetti.map((piece) => (
                    <Box
                        key={piece.id}
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: piece.left,
                            width: piece.size,
                            height: piece.size * 0.6,
                            bgcolor: piece.color,
                            animation: `${fall} 1.8s linear ${piece.delay} infinite`
                        }}
                    />
                ))}

            {variant.id === "pokie" && (
                <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                    {BINGO_LETTERS.map((letter, index) => (
                        <Box
                            key={letter}
                            sx={{
                                width: 72,
                                height: 88,
                                borderRadius: 2,
                                bgcolor: "#1a1a1a",
                                border: "3px solid #ffd54f",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 48,
                                fontWeight: 900,
                                color: index % 2 === 0 ? "#ff5252" : "#ffd54f",
                                animation: `${popIn} 0.35s ease-out ${index * 0.12}s both, ${flash} 0.6s ease-in-out ${0.8 + index * 0.05}s 2`
                            }}>
                            {letter}
                        </Box>
                    ))}
                </Box>
            )}

            {variant.id === "jackpot" && (
                <Box sx={{ display: "flex", gap: 1, mb: 2, overflow: "hidden", height: 72 }}>
                    {BINGO_LETTERS.map((letter, index) => (
                        <Typography
                            key={letter}
                            sx={{
                                fontSize: 64,
                                fontWeight: 900,
                                lineHeight: 1,
                                color: "#3e2723",
                                bgcolor: "#ffe082",
                                px: 1,
                                borderRadius: 1,
                                animation: `${reel} 0.45s ease-out ${index * 0.08}s both`
                            }}>
                            {letter}
                        </Typography>
                    ))}
                </Box>
            )}

            <Typography
                sx={{
                    fontSize: { xs: "4.5rem", md: "7rem" },
                    fontWeight: 900,
                    letterSpacing: "0.12em",
                    lineHeight: 0.9,
                    textShadow: "0 8px 0 rgba(0,0,0,0.25)",
                    color: variant.accent,
                    animation: variant.id === "stamp" ? `${stamp} 0.45s ease-out both` : `${popIn} 0.4s ease-out both`,
                    border: variant.id === "stamp" ? "8px solid currentColor" : "none",
                    borderRadius: variant.id === "stamp" ? "50%" : 0,
                    px: variant.id === "stamp" ? 4 : 0,
                    py: variant.id === "stamp" ? 2 : 0,
                    transform: variant.id === "stamp" ? "rotate(-8deg)" : "none"
                }}>
                BINGO
            </Typography>

            <Typography variant="h5" sx={{ mt: 3, fontWeight: 600, letterSpacing: "0.04em" }}>
                Chart complete
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                {chartLine(count)}
            </Typography>
        </Box>
    );
}
